import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { projectApi } from '@highlight-cp/project-api';

import { projectSlicePrivateActions } from '../../slices/project-slice/project-slice-reducer/project-slice-reducer';
import { projectSliceSelectors } from '../../slices/project-slice/project-slice-selectors';

interface ProjectIdProviderProps {
  children: React.ReactNode;
}

/**
 * ### ProjectIdProvider
 * @description
 * Synchronizes the URL's publicId parameter with the Redux store's project ID.
 * Fetches the project data based on the publicId and updates the Redux state.
 * This should be used once at the root level of applications that need it.
 * See the example below.
 * @example
 * import { ProjectIdProvider } from '@highlight-cp/redux-state';
 *
 * <Provider store={store}>
 *   <ProjectIdProvider>
 *     <YourAppComponent />
 *   </ProjectIdProvider>
 * </Provider>
 */
const getPublicIdFromQuery = (query: ReturnType<typeof useRouter>['query']) => {
  if (typeof query?.publicId === 'string') {
    return query.publicId;
  }
  if (Array.isArray(query?.publicId)) {
    return query.publicId[0];
  }
  return undefined;
};

const ProjectIdProvider = ({ children }: ProjectIdProviderProps) => {
  const dispatch = useDispatch();
  const router = useRouter();
  // Can't use useProjectPublicId hook here because it creates a circular dependency.
  const publicId = getPublicIdFromQuery(router.query);
  const isProjectDetailRoute = router.pathname.includes('[publicId]');
  const currentProjectId = useSelector(projectSliceSelectors.id);
  const { data } = projectApi.useGetProjectsQuery(
    {
      publicId,
    },
    {
      skip: !publicId?.length,
      refetchOnMountOrArgChange: true,
    },
  );
  const project = publicId && data?.[0]?.publicId === publicId ? data?.[0] : null;

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    // Avoid clearing project id during shallow query updates where publicId can be
    // briefly missing while still on a project detail route.
    if (!publicId && currentProjectId && !isProjectDetailRoute) {
      dispatch(projectSlicePrivateActions.unsetId());
    } else if (publicId && project?.id && currentProjectId !== project?.id) {
      dispatch(projectSlicePrivateActions.setId(project?.id));
    }
  }, [currentProjectId, dispatch, isProjectDetailRoute, project?.id, publicId, router.isReady]);

  return children;
};

export default ProjectIdProvider;
